import { Avatar, cn, QelmLockup, QelmSymbol, useTranslation } from '@hbs-us/ui';
import { Link, useLocation } from '@tanstack/react-router';
import { Bell, CreditCard, Languages, LifeBuoy, LogOut, Settings, UserCog } from 'lucide-react';

import { useAccess } from '../../auth/useAccess.js';
import { useFeatureAccess } from '../../auth/useFeatureAccess.js';
import { useSession } from '../../auth/useSession.js';
import { NotificationBell } from '../components/NotificationBell.js';
import { LanguageSwitcher } from '../LanguageSwitcher.js';
import { BAR_TABS } from '../navData.js';
import { CommandPalette } from './CommandPalette.js';
import { Flyout } from './Flyout.js';
import { Launcher } from './Launcher.js';
import { NewMenu } from './NewMenu.js';

/**
 * The platform top bar: launcher · brand · the daily bar tabs, then the action
 * cluster (search · New job · bell · profile/gear menu). Every function the
 * sidebar shell's top bar had is here — identity lives INSIDE the gear menu, and
 * so do Settings and the language preference, instead of the sidebar footer.
 *
 * The bar is ONE row at a FIXED 64px at every width. Nothing wraps: the cluster
 * on the right never shrinks, the tab row absorbs the slack in the middle, and
 * the pieces that cannot fit step aside in a decided order as the viewport
 * narrows — the wordmark below `lg`, the tab row below `md` (phones get the
 * launcher only), the search caption below `lg`, the New job label below `sm`.
 *
 * The active tab is the sidebar's orange pill (bg-sidebar-accent) — the one
 * accent moment in the chrome, carried over unchanged.
 */

// A bar tab is the kit SidebarItem laid flat: same 44px row, radius, type,
// focus ring and orange active pill, so the bar and the rail read as one product.
//
// ⛔ THE FOCUS RING IS `ring-ring` PLUS AN EXPLICIT `ring-offset-<surface>`, EVERY
// TIME. Two things were wrong and one of them shipped a visible defect:
//
//  1. `--ring` and `--sidebar-ring` hold the SAME value in both themes (orange-700
//     light, orange-500 dark). Two names for one value is the drift `labels.ts`
//     warns about; the chrome is not the sidebar, so it uses the chrome's name.
//  2. Tailwind v4 initialises `--tw-ring-offset-color` to `#fff`, so a bare
//     `ring-offset-2` draws a 2px WHITE gap. Measured on this branch in dark mode:
//     `box-shadow: rgb(255,255,255) 0 0 0 2px, rgb(255,90,31) 0 0 0 4px` — a white
//     halo around every focused tab on a near-black bar. The offset must name the
//     surface the control actually sits on (`card` in the bar, `popover` in a
//     flyout) or it invents a colour that is in neither theme.
const BAR_TAB_CLASS =
  'flex h-11 shrink-0 items-center rounded-md px-2 text-sm font-medium transition-colors lg:px-3 ' +
  'duration-[var(--dur-quick)] ease-[var(--ease-out)] motion-reduce:transition-none ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-card';

// 44px, like every other control in this chrome — a menu row is not a smaller
// class of target just because it lives in a panel (A11Y-01).
//
// A menu row is LABEL-led: the words are what you read, so they carry the full
// ink and the icon stays the quiet second rank. (A launcher tile is ICON-led and
// therefore does the opposite — see `TILE_CLASS`. Same principle, read off which
// element is doing the work.) Muting the labels made the whole panel one grey
// wash in which nothing was the content.
const MENU_ITEM_CLASS =
  'flex h-11 w-full items-center gap-2.5 rounded-md px-2.5 text-sm font-medium text-foreground ' +
  'transition-colors duration-[var(--dur-quick)] ease-[var(--ease-out)] motion-reduce:transition-none ' +
  'hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-popover';

const MENU_ICON_CLASS = 'size-4 shrink-0 text-muted-foreground';

function Brand() {
  const { t } = useTranslation();
  return (
    <Link
      to="/"
      aria-label={t('nav:appName')}
      className="flex h-11 shrink-0 items-center rounded-md px-1 text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
    >
      {/*
        ONE lockup, not two `<img>` inks racing a `.dark` selector. The kit mark is
        inline SVG whose letterforms take `currentColor`, so `text-foreground` on
        this link is the whole theme story — and the wordmark stops changing SIZE
        with the theme, which it did because the two baked assets shipped different
        viewBoxes (measured: 140px wide in dark against 125px in light at the same
        `h-7`). The link carries the accessible name, so both marks render
        decorative and "QELM" is announced exactly once.

        The wordmark is held back to `xl` — measured, not assumed. From the moment
        the tab row appears at `md` until 1280px the lockup is the widest thing in
        the bar that carries no task, and at 1024 in Spanish it is exactly what
        pushes the fourth tab off the end. So the bar wears the mark alone wherever
        the tabs need the room, and the wordmark returns once there is space for both.
      */}
      <QelmSymbol className="size-7 xl:hidden" />
      <QelmLockup className="hidden h-7 xl:block" />
    </Link>
  );
}

/** The daily destinations (draft list — one line in navData.ts to change later). */
function BarTabs() {
  const { t } = useTranslation();
  const location = useLocation();
  const canAccess = useAccess();
  const canSeeFeature = useFeatureAccess();

  return (
    <nav
      aria-label={t('nav:appName')}
      /*
        `py-1.5` is the focus ring's room, not decoration. `overflow-x-auto` is the
        safety net for a locale wide enough to overrun the row — but CSS will not
        let one axis scroll while the other stays visible, so the nav clipped its
        own children VERTICALLY too, and a focused tab's 2px gap + 2px ring were
        sheared off top and bottom: the keyboard user saw two orange arcs and no
        ring. 6px each side clears the 4px the indicator needs.
      */
      className="hidden min-w-0 shrink items-center gap-1 overflow-x-auto px-1 py-1.5 md:flex lg:px-2"
    >
      {BAR_TABS.filter((tab) => canSeeFeature(tab.to))
        .filter((tab) => canAccess(tab.access))
        .map((tab) => {
          const active = location.pathname === tab.to || location.pathname.startsWith(`${tab.to}/`);
          return (
            <Link
              key={tab.to}
              to={tab.to}
              aria-current={active ? 'page' : undefined}
              className={cn(
                BAR_TAB_CLASS,
                active
                  ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                  : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
              )}
            >
              {t(tab.labelKey)}
            </Link>
          );
        })}
    </nav>
  );
}

function AccountMenu() {
  const { t } = useTranslation();
  const { session, signOut } = useSession();
  const canAccess = useAccess();
  const userName = session?.user.name ?? '';

  return (
    <Flyout
      label={t('nav:profile.openMenu')}
      /*
       * The trigger is the person, not a cog. A gear promised settings and
       * delivered an account menu, and it wore the SAME lucide glyph as the
       * "Company settings" row inside it — one icon, two meanings, one bar. The
       * avatar names the menu the way its accessible name always did, and it puts
       * identity in the bar instead of one click behind it.
       *
       * The open state cannot be the shared `bg-secondary` here: `--muted` (the
       * avatar's own fill) and `--secondary` are the same value, so an open
       * avatar trigger would dissolve into its own button. It gets a ring off
       * `aria-expanded` instead — the state the button already publishes.
       */
      trigger={<Avatar name={userName} size="sm" aria-hidden="true" />}
      triggerClassName={
        // Round, because what it holds is round: a square ring drawn around a
        // circular chip leaves four corner gaps and reads as two shapes fighting.
        'rounded-full aria-expanded:ring-2 aria-expanded:ring-ring ' +
        'aria-expanded:ring-offset-2 aria-expanded:ring-offset-card'
      }
      panelClassName="right-0 origin-top-right w-72"
    >
      {/* One left edge for all three lines: the workspace you are in, then who you
          are in it. The avatar is 8px above on the trigger, so repeating it here
          would only cost the email the width it needs to be read. */}
      <div className="border-border border-b px-2.5 pb-2.5 pt-1.5">
        <span className="block truncate font-semibold text-foreground text-sm">
          {session?.tenant.name ?? ''}
        </span>
        <span className="mt-1 block truncate text-foreground text-xs">{userName}</span>
        <span className="block truncate text-muted-foreground text-xs">
          {session?.user.email ?? ''}
        </span>
      </div>
      <div className="flex flex-col gap-0.5 pt-1.5">
        <Link to="/account" className={MENU_ITEM_CLASS}>
          <UserCog className={MENU_ICON_CLASS} aria-hidden="true" />
          <span>{t('nav:items.userSettings')}</span>
        </Link>
        {/* Company settings is a real destination (`GET /api/v1/settings` -> settings.view),
            so it is gated like one; needing help is not a privilege. */}
        {canAccess({ permission: 'settings.view' }) ? (
          <>
            <Link to="/settings" className={MENU_ITEM_CLASS}>
              <Settings className={MENU_ICON_CLASS} aria-hidden="true" />
              <span>{t('nav:items.companySettings')}</span>
            </Link>
          </>
        ) : null}
        {/* Everyone's own alert choices, so not behind settings.view. */}
        <Link to="/settings/notifications" className={MENU_ITEM_CLASS}>
          <Bell className={MENU_ICON_CLASS} aria-hidden="true" />
          <span>{t('nav:items.notificationSettings')}</span>
        </Link>
        {canAccess({ permission: 'billing.view' }) ? (
          <Link to="/billing" className={MENU_ITEM_CLASS}>
            <CreditCard className={MENU_ICON_CLASS} aria-hidden="true" />
            <span>{t('nav:items.qelmSubscription')}</span>
          </Link>
        ) : null}
        <Link to="/support" className={MENU_ITEM_CLASS}>
          <LifeBuoy className={MENU_ICON_CLASS} aria-hidden="true" />
          <span>{t('nav:items.qelmSupport')}</span>
        </Link>
      </div>
      {/* Language is a preference, not a destination, so it lives with the other
          account preferences instead of standing in the bar as a sixth control.
          It is written as one of THIS menu's rows — same icon column, same label
          treatment — because it was the only line in the panel wearing tracked
          caps, aligned to the icons while every real label sat 34px to its right.
          The visible caption is decorative: the <select> carries its own name. */}
      <div className="mt-1.5 border-border border-t pt-1.5">
        <div className="flex items-center justify-between gap-2 px-2.5 py-1">
          <span
            className="flex items-center gap-2.5 font-medium text-foreground text-sm"
            aria-hidden="true"
          >
            <Languages className={MENU_ICON_CLASS} />
            <span>{t('nav:language.label')}</span>
          </span>
          <LanguageSwitcher />
        </div>
      </div>
      {/* Sign out ends the session — the one row here that is not a place you can
          come back from with the Back button, so it gets its own rule. */}
      <div className="mt-1.5 flex flex-col gap-0.5 border-border border-t pt-1.5">
        <button type="button" className={MENU_ITEM_CLASS} onClick={() => void signOut()}>
          <LogOut className={MENU_ICON_CLASS} aria-hidden="true" />
          <span>{t('nav:topbar.signOut')}</span>
        </button>
      </div>
    </Flyout>
  );
}

export function TopBar() {
  return (
    <header className="flex h-16 shrink-0 items-center gap-2 border-border border-b bg-card px-4 lg:gap-3">
      <Brand />
      <BarTabs />
      {/*
        The bar is three columns, not two clusters pinned to the rims. The tab row
        used to be `flex-1` and swallow every spare pixel, which parked a MEASURED
        422px of nothing between "Clients" and a search field held at 240px — the
        one control in this bar that gets better with width, starved so a nav could
        hold empty space. The slack now belongs to the cluster, and inside it to the
        search, up to a cap; past that the gutter reads as a gutter.
      */}
      <div className="ml-auto flex min-w-0 flex-1 items-center justify-end gap-2 lg:gap-3">
        <CommandPalette />
        {/* Four create actions behind one button, each carrying the guard its own POST
            applies — the rules and why they do not match the `*.create` keys are in
            `NEW_ACTIONS`. It renders nothing for a person who may create nothing. */}
        <NewMenu />
        {/* The three standing chrome buttons sit tight against each other so they read
            as one set of controls, not three more items in the row.

            The launcher used to open the bar, on the left of the wordmark, where it read
            as part of the brand rather than as a control. It belongs with the other
            things that are always there whatever page you are on — and putting it on the
            right leaves the left edge to say only what this product is and where you are
            in it. Every app grid a person already knows lives on this side. */}
        <div className="flex shrink-0 items-center gap-0.5">
          <Launcher />
          <NotificationBell />
          <AccountMenu />
        </div>
      </div>
    </header>
  );
}
